import React from 'react';
import { cn } from '../../lib/utils';

const STRIPE_DROPPED =
  'repeating-linear-gradient(-45deg, #D7E3F4, #D7E3F4 4px, #EEF3FA 4px, #EEF3FA 8px)';
const STRIPE_PENDING =
  'repeating-linear-gradient(-45deg, #E8D7A8, #E8D7A8 4px, #F6EDD4 4px, #F6EDD4 8px)';

export function FunnelStageBar({
  stage,
  maxTotal,
  selected = false,
  isWorst = false,
  onSelect,
}) {
  const widthPct = maxTotal > 0 ? Math.max((Number(stage.total || 0) / maxTotal) * 100, 10) : 0;
  const converted = Number(stage.converted || 0);
  const dropped = Number(stage.dropped || 0);
  const pending = Number(stage.pending || 0);
  const segmentTotal = Math.max(converted + dropped + pending, 1);
  const convertedPct = stage.unavailable ? 0 : (converted / segmentTotal) * 100;
  const droppedPct = stage.unavailable ? 0 : (dropped / segmentTotal) * 100;
  const pendingPct = (pending / segmentTotal) * 100;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(stage.id)}
      className={cn(
        'w-full rounded-xl border px-3 py-2.5 text-left transition sm:px-4',
        selected
          ? 'border-[var(--brand-primary,#1A428A)] bg-[var(--brand-primary,#1A428A)]/[0.05] shadow-sm'
          : isWorst
            ? 'border-rose-200/80 bg-rose-50/40 hover:border-rose-300'
            : 'border-transparent hover:border-slate-200 hover:bg-slate-50/80'
      )}
      aria-pressed={selected}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-[#201C35]">
          <span className="mr-2 tabular-nums text-slate-400">{stage.index}</span>
          {stage.label}
          {stage.subtitle ? (
            <span className="ml-2 text-xs font-medium text-slate-400">{stage.subtitle}</span>
          ) : null}
          {isWorst ? (
            <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-700">
              Weak point
            </span>
          ) : null}
        </p>
        <p className="text-sm font-bold tabular-nums text-[#201C35]">
          {stage.unavailable ? '—' : Number(stage.converted || 0).toLocaleString('en-US')}
          {stage.conversionLabel ? (
            <span className="ml-2 text-xs font-semibold text-slate-500">{stage.conversionLabel}</span>
          ) : null}
        </p>
      </div>

      <div className="mt-2.5 h-10 w-full">
        <div
          className="flex h-full overflow-hidden rounded-lg"
          style={{ width: `${widthPct}%` }}
        >
          {convertedPct > 0 ? (
            <div
              className="h-full bg-[var(--brand-primary,#1A428A)]"
              style={{ width: `${convertedPct}%` }}
              title="Converted"
            />
          ) : null}
          {droppedPct > 0 ? (
            <div
              className="h-full"
              style={{ width: `${droppedPct}%`, backgroundImage: STRIPE_DROPPED }}
              title={stage.droppedLabel || 'Dropped'}
            />
          ) : null}
          {pendingPct > 0 ? (
            <div
              className="h-full"
              style={{ width: `${pendingPct}%`, backgroundImage: STRIPE_PENDING }}
              title={stage.pendingLabel || 'Pending'}
            />
          ) : null}
        </div>
      </div>

      {(stage.pendingLabel || (stage.droppedLabel && !isWorst)) ? (
        <div className="mt-1.5 flex flex-wrap gap-x-3 text-[11px] text-slate-500">
          {stage.pendingLabel ? <span className="text-amber-800">{stage.pendingLabel}</span> : null}
        </div>
      ) : null}
    </button>
  );
}
